import React, { useState, useEffect } from 'react';
import API from '../api/api';

function CreateShipmentModal({ isOpen, onClose, onShipmentCreated }) {
  const [formData, setFormData] = useState({
    po_number: '',
    cust_po_number: '',
    shipper: '',
    carrier: '',
    pickup_date: '',
    pickup_time: '',
    delivery_date: '',
    delivery_time: '',
    equipment_type: '',
    rate: '',
    special_instructions: '',
    origin_name: '',
    origin_address: '',
    destination_name: '',
    destination_address: '',
    trailer_number: '',
    truck_number: ''
  });
  
  const [organizations, setOrganizations] = useState([]);
  const [tradingPartners, setTradingPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);       // submission errors only
  const [fetchWarning, setFetchWarning] = useState(null); // background fetch failures
  const [success, setSuccess] = useState(false);

  const equipmentTypes = [
    'DRY_VAN', 'REEFER', 'FLATBED', 'POWER_ONLY', 'STEP_DECK', 
    'CONESTOGA', 'LOWBOY', 'TANKER', 'BULK', 'OTHER'
  ];

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    resetForm();
    fetchOrganizations(controller.signal);
    fetchTradingPartners(controller.signal);
    fetchNextPoNumber();
    // Cancel any still-pending fetch if the modal closes before they finish,
    // preventing a stale error callback from firing after a fresh submit.
    return () => controller.abort();
  }, [isOpen]);

  const fetchOrganizations = async (signal) => {
    try {
      const response = await API.get('organizations/', { signal });
      const orgsData = response.data.results || response.data;
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('Error fetching organizations:', err);
      // Use fetchWarning, not error, so a background fetch failure never
      // overwrites or races with the submission error state.
      setFetchWarning('Could not load carriers — you may need to refresh.');
    }
  };

  const fetchTradingPartners = async (signal) => {
    try {
      const response = await API.get('trading-partners/', { signal });
      const partnersData = response.data.results || response.data;
      setTradingPartners(Array.isArray(partnersData) ? partnersData : []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('Error fetching trading partners:', err);
      setFetchWarning('Could not load shippers — you may need to refresh.');
    }
  };

  const fetchNextPoNumber = async () => {
    try {
      const response = await API.get('shipments/next-po-number/');
      setFormData(prev => ({
        ...prev,
        po_number: response.data.next_po_number
      }));
    } catch (err) {
      console.error('Error fetching next PO number:', err);
      // Don't show error to user, just log it
    }
  };

  const resetForm = () => {
    setFormData({
      po_number: '', // Will be auto-populated by fetchNextPoNumber
      cust_po_number: '',
      shipper: '',
      carrier: '',
      pickup_date: '',
      pickup_time: '',
      delivery_date: '',
      delivery_time: '',
      equipment_type: '',
      rate: '',
      special_instructions: '',
      origin_name: '',
      origin_address: '',
      destination_name: '',
      destination_address: '',
      trailer_number: '',
      truck_number: ''
    });
    setError(null);
    setFetchWarning(null);
    setSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCreateShipmentError = (data) => {
    if (!data) return 'Failed to create shipment';
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) return data.detail.map(String).join('; ');
    if (data.detail != null) return JSON.stringify(data.detail);
    if (typeof data.error === 'string') return data.error;
    if (data.error != null) return JSON.stringify(data.error);
    if (typeof data === 'object') {
      const parts = Object.entries(data).map(([k, v]) => {
        const s = Array.isArray(v) ? v.join(', ') : String(v);
        return `${k}: ${s}`;
      });
      if (parts.length) return parts.join('; ');
    }
    return 'Failed to create shipment';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const shipperId = String(formData.shipper || '').trim();
    const carrierId = String(formData.carrier || '').trim();
    if (!shipperId || !carrierId) {
      setError('Please select a shipper and a carrier.');
      setLoading(false);
      return;
    }

    const carrierPk = parseInt(carrierId, 10);
    if (Number.isNaN(carrierPk)) {
      setError('Invalid carrier selection.');
      setLoading(false);
      return;
    }

    // Build payload: only writable API fields. Omit empty decimals — Shipment.rate is non-null and
    // sending null causes a 400 from the serializer.
    const payload = {
      shipper: shipperId,
      carrier: carrierPk,
      pickup_date: formData.pickup_date || null,
      delivery_date: formData.delivery_date || null,
      status: 'pending',
    };

    if (formData.pickup_time) payload.pickup_time = formData.pickup_time;
    if (formData.delivery_time) payload.delivery_time = formData.delivery_time;
    if (formData.cust_po_number?.trim()) payload.cust_po_number = formData.cust_po_number.trim();
    if (formData.equipment_type) payload.equipment_type = formData.equipment_type;
    if (formData.special_instructions?.trim()) payload.special_instructions = formData.special_instructions.trim();
    if (formData.trailer_number?.trim()) payload.trailer_number = formData.trailer_number.trim();
    if (formData.truck_number?.trim()) payload.truck_number = formData.truck_number.trim();

    const rateStr = formData.rate != null ? String(formData.rate).trim() : '';
    if (rateStr !== '') {
      const n = Number(rateStr);
      if (!Number.isNaN(n)) payload.rate = String(n);
    }

    if (formData.origin_name?.trim()) payload.create_pickup_name = formData.origin_name.trim();
    if (formData.origin_address?.trim()) payload.create_pickup_address = formData.origin_address.trim();
    if (formData.destination_name?.trim()) payload.create_delivery_name = formData.destination_name.trim();
    if (formData.destination_address?.trim()) payload.create_delivery_address = formData.destination_address.trim();

    try {
      const response = await API.post('shipments/', payload);
      setSuccess(true);
      setTimeout(() => {
        onShipmentCreated(response.data);
        onClose();
        resetForm();
      }, 1500);
    } catch (err) {
      console.error('Error creating shipment:', err);
      setError(formatCreateShipmentError(err.response?.data));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New Shipment</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {fetchWarning && !success && (
            <div className="mb-4 rounded-md bg-yellow-50 p-3 border border-yellow-200">
              <p className="text-sm text-yellow-800">{fetchWarning}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Shipment created successfully!
                  </h3>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PO Number */}
              <div>
                <label htmlFor="po_number" className="block text-sm font-medium text-gray-700">
                  PO Number <span className="text-xs text-gray-500">(Auto-generated)</span>
                </label>
                <input
                  type="text"
                  name="po_number"
                  id="po_number"
                  value={formData.po_number}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-gray-50"
                  placeholder="Auto-generated PO number"
                />
              </div>

              {/* Customer PO Number */}
              <div>
                <label htmlFor="cust_po_number" className="block text-sm font-medium text-gray-700">
                  Customer PO Number
                </label>
                <input
                  type="text"
                  name="cust_po_number"
                  id="cust_po_number"
                  value={formData.cust_po_number}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter customer PO number"
                />
              </div>

              {/* Shipper */}
              <div>
                <label htmlFor="shipper" className="block text-sm font-medium text-gray-700">
                  Shipper *
                </label>
                <select
                  name="shipper"
                  id="shipper"
                  required
                  value={formData.shipper}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Shipper</option>
                  {tradingPartners.map(partner => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name || partner.edi_id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Carrier */}
              <div>
                <label htmlFor="carrier" className="block text-sm font-medium text-gray-700">
                  Carrier *
                </label>
                <select
                  name="carrier"
                  id="carrier"
                  required
                  value={formData.carrier}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Carrier</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.carrier_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Pickup Date */}
              <div>
                <label htmlFor="pickup_date" className="block text-sm font-medium text-gray-700">
                  Pickup Date *
                </label>
                <input
                  type="date"
                  name="pickup_date"
                  id="pickup_date"
                  required
                  value={formData.pickup_date}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Pickup Time */}
              <div>
                <label htmlFor="pickup_time" className="block text-sm font-medium text-gray-700">
                  Pickup Time
                </label>
                <input
                  type="time"
                  name="pickup_time"
                  id="pickup_time"
                  value={formData.pickup_time}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Delivery Date */}
              <div>
                <label htmlFor="delivery_date" className="block text-sm font-medium text-gray-700">
                  Delivery Date *
                </label>
                <input
                  type="date"
                  name="delivery_date"
                  id="delivery_date"
                  required
                  value={formData.delivery_date}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Delivery Time */}
              <div>
                <label htmlFor="delivery_time" className="block text-sm font-medium text-gray-700">
                  Delivery Time
                </label>
                <input
                  type="time"
                  name="delivery_time"
                  id="delivery_time"
                  value={formData.delivery_time}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Equipment Type */}
              <div>
                <label htmlFor="equipment_type" className="block text-sm font-medium text-gray-700">
                  Equipment Type
                </label>
                <select
                  name="equipment_type"
                  id="equipment_type"
                  value={formData.equipment_type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Equipment Type</option>
                  {equipmentTypes.map(type => (
                    <option key={type} value={type}>
                      {type.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rate */}
              <div>
                <label htmlFor="rate" className="block text-sm font-medium text-gray-700">
                  Rate ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="rate"
                  id="rate"
                  value={formData.rate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Trailer Number */}
              <div>
                <label htmlFor="trailer_number" className="block text-sm font-medium text-gray-700">
                  Trailer Number
                </label>
                <input
                  type="text"
                  name="trailer_number"
                  id="trailer_number"
                  value={formData.trailer_number}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Truck Number */}
              <div>
                <label htmlFor="truck_number" className="block text-sm font-medium text-gray-700">
                  Truck Number
                </label>
                <input
                  type="text"
                  name="truck_number"
                  id="truck_number"
                  value={formData.truck_number}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Pickup Location */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Pickup Location</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="origin_name" className="block text-sm font-medium text-gray-700">
                    Pickup Location Name
                  </label>
                  <input
                    type="text"
                    name="origin_name"
                    id="origin_name"
                    value={formData.origin_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="origin_address" className="block text-sm font-medium text-gray-700">
                    Pickup Address
                  </label>
                  <textarea
                    name="origin_address"
                    id="origin_address"
                    rows="2"
                    value={formData.origin_address}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Location */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Delivery Location</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="destination_name" className="block text-sm font-medium text-gray-700">
                    Delivery Location Name
                  </label>
                  <input
                    type="text"
                    name="destination_name"
                    id="destination_name"
                    value={formData.destination_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="destination_address" className="block text-sm font-medium text-gray-700">
                    Delivery Address
                  </label>
                  <textarea
                    name="destination_address"
                    id="destination_address"
                    rows="2"
                    value={formData.destination_address}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <label htmlFor="special_instructions" className="block text-sm font-medium text-gray-700">
                Special Instructions
              </label>
              <textarea
                name="special_instructions"
                id="special_instructions"
                rows="3"
                value={formData.special_instructions}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter any special instructions for this shipment..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Shipment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateShipmentModal; 